import React, { useState } from 'react';
import PageTemplate from '../components/PageTemplate';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    setFormErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        console.log('Form submitted:', formData);
        setIsSubmitting(false);
        setSubmitSuccess(true);
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      }, 1500);
    }
  };

  const contactMethods = [
    {
      icon: '📧',
      title: 'Email Us',
      description: 'For general inquiries and support',
      contact: '',
      action: 'Email',
      link: '#'
    },
    {
      icon: '📞',
      title: 'Call Us',
      description: 'Monday-Friday, 9am-5pm EST',
      contact: '',
      action: 'Call',
      link: '#'
    },
    {
      icon: '💬',
      title: 'Live Chat',
      description: 'Available during business hours',
      contact: 'Start a conversation now',
      action: 'Chat Now',
      link: '#chat'
    }
  ];

  return (
    <PageTemplate title="Contact Us">
      <div className="grid grid-cols-2 grid-lg-cols-2 gap-lg">
        <div className="grid-lg-cols-2">
          <section className="mb-5">
          <div className="card">
            <h2 className="mb-4">Get in Touch</h2>
            <p className="mb-4">
              We're here to help with any questions or feedback you may have about Patients Empowered. 
              Fill out the form below, and our team will get back to you as soon as possible.
            </p>
            
            {submitSuccess && (
              <div className="alert alert-success mb-4">
                <p className="mb-0">Thank you for your message! We'll get back to you soon.</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="card">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Your Name <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-control ${formErrors.name ? 'error' : ''}`}
                />
                {formErrors.name && <div className="form-error">{formErrors.name}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-control ${formErrors.email ? 'error' : ''}`}
                />
                {formErrors.email && <div className="form-error">{formErrors.email}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  Subject <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`form-control ${formErrors.subject ? 'error' : ''}`}
                />
                {formErrors.subject && <div className="form-error">{formErrors.subject}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-control form-textarea ${formErrors.message ? 'error' : ''}`}
                  rows="5"
                />
                {formErrors.message && <div className="form-error">{formErrors.message}</div>}
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
            </div>
          </section>
        </div>
        
        
        <div>
          <section className="mb-5">
          <div className="card">
            <h2 className="mb-4">Contact Methods</h2>

            <div className="grid pb-6 grid-cols-1 gap-md">
              {contactMethods.map((method, index) => (
                <div key={index} className="card">
                  <div className="flex gap-md">
                    <div className="text-center" style={{ fontSize: '2rem', minWidth: '60px' }}>
                      {method.icon}
                    </div>
                    <div>
                      <h3 className="mb-1">{method.title}</h3>
                      <p className="mb-2">{method.description}</p>
                      <p className="mb-3 font-bold">{method.contact}</p>
                      <a href={method.link} className="btn btn-secondary btn-sm">{method.action}</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </section>
        </div>
      </div>
    </PageTemplate>
  );
};

export default ContactPage;
